import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { config } from '@/config';
import { tokenStore } from '@/auth/tokenStore';
import { refreshAccessToken } from '@/auth/refreshManager';
import {
  ApiError,
  AuthExpiredError,
  NetworkError,
  TimeoutError,
  type ApiEnvelope,
} from './errors';
import { DEFAULT_RETRY, DEFAULT_TIMEOUT_MS, type RequestPolicy } from './policy';
import { backoffDelayMs, isRetryableError, parseRetryAfter, retryAfterMs } from './retry';

// Низкоуровневый axios. Прямые вызовы (rawAxios.*) идут МИМО runner — без auth/refresh/retry.
// Их использует только refreshManager (чтобы не зациклить refresh). Весь остальной код ходит
// через request(): единственная точка с авторизацией, таймаутами, повторами и refresh-на-401.
export const rawAxios = axios.create({
  baseURL: config.apiBaseUrl,
});

// Content-Type ставим осознанно: application/json только для plain-object тела. FormData (загрузка
// файлов) и URLSearchParams (form-urlencoded) НЕ трогаем — их content-type (с boundary) выставляет
// axios/браузер. Поэтому дефолтного JSON на инстансе нет.
function isPlainJsonBody(data: unknown): boolean {
  return (
    data !== undefined &&
    data !== null &&
    typeof data === 'object' &&
    !(data instanceof FormData) &&
    !(data instanceof URLSearchParams) &&
    !(data instanceof Blob) &&
    !(data instanceof ArrayBuffer)
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function normalizeError(error: unknown): NetworkError | TimeoutError | ApiError {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new TimeoutError(error.message);
    }
    if (!error.response) {
      return new NetworkError(error.message, error);
    }
    const { status, data, headers } = error.response;
    const envelope = data && typeof data === 'object' ? (data as ApiEnvelope) : undefined;
    const retryAfter = parseRetryAfter(headers?.['retry-after'] as string | undefined, Date.now());
    return new ApiError(status, `Request failed with status ${status}`, {
      envelope,
      retryAfterMs: retryAfter,
    });
  }
  return new NetworkError('Unknown network error', error);
}

/**
 * Единая точка выполнения HTTP-запросов. Возвращает полный AxiosResponse (сервисы могут
 * читать status/конверт), либо бросает нормализованную ошибку (ApiError/NetworkError/TimeoutError/
 * AuthExpiredError). Поведение задаётся политикой: auth, idempotent, retry, timeout, signal.
 */
export async function request<T = unknown>(
  reqConfig: AxiosRequestConfig,
  policy: RequestPolicy = {},
): Promise<AxiosResponse<T>> {
  const method = (reqConfig.method ?? 'get').toLowerCase();
  const idempotent = policy.idempotent ?? method === 'get';
  const retryPolicy =
    policy.retry === false ? null : (policy.retry ?? (idempotent ? DEFAULT_RETRY : null));
  const useAuth = policy.auth !== false;

  let retries = 0;
  let refreshed = false;

  for (;;) {
    // Заголовки пересобираем каждую попытку: токен мог обновиться после refresh.
    const headers: Record<string, string> = { ...(reqConfig.headers as Record<string, string>) };
    headers['X-Correlation-ID'] = crypto.randomUUID();
    if (useAuth) {
      const token = tokenStore.getAccess();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    if (policy.idempotencyKey) {
      headers['Idempotency-Key'] = policy.idempotencyKey;
    }
    if (!headers['Content-Type'] && isPlainJsonBody(reqConfig.data)) {
      headers['Content-Type'] = 'application/json';
    }

    try {
      return await rawAxios.request<T>({
        ...reqConfig,
        headers,
        timeout: policy.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        signal: policy.signal,
      });
    } catch (error) {
      // Отмена по AbortSignal — пробрасываем как есть, не повторяем.
      if (axios.isCancel(error)) {
        throw error;
      }

      // 401 на авторизованном запросе → single-flight refresh → одна доп. попытка (не «retry»).
      if (useAuth && !refreshed && axios.isAxiosError(error) && error.response?.status === 401) {
        refreshed = true;
        const newToken = await refreshAccessToken();
        if (newToken) {
          continue;
        }
        throw new AuthExpiredError();
      }

      const normalized = normalizeError(error);
      if (retryPolicy && retries < retryPolicy.attempts && isRetryableError(normalized)) {
        retries += 1;
        await sleep(retryAfterMs(normalized) ?? backoffDelayMs(retries, retryPolicy));
        continue;
      }
      throw normalized;
    }
  }
}
